/**
 * ResultsScreen — the generated-templates view. Every card is backed by a
 * REAL compiled Editor Document that passed (or was gated by) the Quality
 * Validation Engine. There are no placeholder cards.
 *
 * Card previews use the existing PageThumbnailPreview (the app's own
 * template render path) — nothing is drawn by hand. "Open in Editor" hands
 * the compiled document to the live editor through its existing loading
 * flow. Publish and Open are gated on `publishable`; rejected documents
 * still appear, showing their validation report.
 */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import PageThumbnailPreview from '../../../components/Editor/PageThumbnailPreview';
import {
  Eye, PenSquare, Upload, Trash2, CheckCircle2, AlertTriangle, Sparkles,
  RotateCcw, X, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import { useEditorHandoff } from '../../hooks/useEditorHandoff';
import { publishTemplate, deletePublishedTemplate } from '../../api/publishTemplate';

const Chip = ({ label, value }) => (
  <span className="inline-flex items-center rounded-full bg-gray-50 border border-gray-100 text-gray-600 px-2 py-0.5 text-[11px]">
    {label}: <b className="ml-1 text-gray-800">{value}</b>
  </span>
);

const StatusBadge = ({ publishable }) => (
  publishable ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-medium">
      <ShieldCheck className="w-3 h-3" /> Publishable
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[11px] font-medium">
      <ShieldAlert className="w-3 h-3" /> Needs review
    </span>
  )
);

/** Modal reused for both Preview (large render) and the validation report. */
const CardModal = ({ card, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose} data-testid="result-modal">
    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          {card.name} <StatusBadge publishable={card.publishable} />
        </h3>
        <button onClick={onClose} aria-label="Close preview"><X className="w-4 h-4 text-gray-400" /></button>
      </div>
      <div className="p-5">
        <div className="mx-auto mb-5 border border-gray-200 rounded-lg overflow-hidden" style={{ maxWidth: 360 }}>
          <div style={{ position: 'relative', width: '100%', paddingBottom: `${(card.canvasSize.height / card.canvasSize.width) * 100}%` }}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <PageThumbnailPreview page={card.page} canvasSize={card.canvasSize} fillContainer />
            </div>
          </div>
        </div>

        <div className="text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-900">Quality {card.qualityScore}/100</span>
            <StatusBadge publishable={card.publishable} />
          </div>
          {card.quality.errors.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-red-600 mb-1">Errors ({card.quality.errors.length})</p>
              <ul className="space-y-0.5">
                {card.quality.errors.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" /> {f.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {card.quality.improvementSuggestions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Suggestions</p>
              <ul className="space-y-0.5">
                {card.quality.improvementSuggestions.map((s, i) => (
                  <li key={i} className="text-xs text-gray-500">• {s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const ResultCard = ({ card, selected, onToggleSelect, onPublish, onDelete, onOpenEditor, published, onPreview }) => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="template-card">
    <div className="relative bg-gray-100" style={{ paddingBottom: `${Math.min(140, (card.canvasSize.height / card.canvasSize.width) * 100)}%` }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <PageThumbnailPreview page={card.page} canvasSize={card.canvasSize} fillContainer />
      </div>
      <label className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90 rounded px-1.5 py-0.5 text-[11px] text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          disabled={!card.publishable}
          className="rounded border-gray-300"
          aria-label={`Select ${card.name}`}
        />
        Select
      </label>
      {published && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-500 text-white rounded-full px-2 py-0.5 text-[11px] font-medium">
          <CheckCircle2 className="w-3 h-3" /> Published
        </span>
      )}
    </div>

    <div className="p-4">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-sm font-semibold text-gray-900">{card.name}</h3>
        <span className={`text-sm font-bold ${card.publishable ? 'text-emerald-600' : 'text-amber-600'}`}>
          {card.qualityScore}<span className="text-[10px] text-gray-400">/100</span>
        </span>
      </div>
      <div className="mb-2"><StatusBadge publishable={card.publishable} /></div>
      <div className="flex flex-wrap gap-1 mb-3">
        <Chip label="Type" value={card.documentType} />
        <Chip label="Category" value={card.category} />
      </div>
      <p className="text-[11px] text-gray-400 mb-3" title="Creative DNA">DNA: {card.dnaSummary}</p>

      <div className="grid grid-cols-4 gap-1">
        <Button variant="outline" size="sm" onClick={() => onPreview(card)} aria-label={`Preview ${card.name}`}>
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => onOpenEditor(card)}
          disabled={!card.publishable}
          aria-label={`Open ${card.name} in editor`}
          title={card.publishable ? 'Open in editor' : 'Passes validation before it can be opened'}
        >
          <PenSquare className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => onPublish([card.id])}
          disabled={!card.publishable || published}
          aria-label={`Publish ${card.name}`}
          title={card.publishable ? 'Publish' : 'Fix validation errors to publish'}
        >
          <Upload className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline" size="sm"
          className="text-red-600 hover:text-red-700"
          onClick={() => onDelete(card.id)}
          aria-label={`Delete ${card.name}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  </div>
);

const ResultsScreen = ({ results, brief = {}, onGenerateMore }) => {
  const [cards, setCards] = useState(results);
  const [selected, setSelected] = useState(new Set());
  // id → persisted template id. A card is "published" only once the server has
  // stored it and returned an id; before this the button flipped to Published
  // on a local Set and the template was gone on refresh.
  const [publishedIds, setPublishedIds] = useState({});
  const [busy, setBusy] = useState(new Set());
  const [publishError, setPublishError] = useState(null);
  const [previewCard, setPreviewCard] = useState(null);
  const { openInEditor } = useEditorHandoff();

  const published = new Set(Object.keys(publishedIds));

  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Publish only publishable cards — the Quality Gate is the source of truth.
  // Each is saved through the existing admin template API; a failure is
  // reported and the card stays unpublished rather than showing a success it
  // did not have.
  const publish = async (ids) => {
    const pending = ids.filter((id) => {
      const card = cards.find((c) => c.id === id);
      return card && card.publishable && !publishedIds[id] && !busy.has(id);
    });
    if (!pending.length) return;

    setPublishError(null);
    setBusy((prev) => new Set([...prev, ...pending]));

    const failures = [];
    for (const id of pending) {
      const card = cards.find((c) => c.id === id);
      try {
        // Sequential on purpose: a batch publish that fires ten creates at once
        // turns one expired session into ten identical error toasts.
        // eslint-disable-next-line no-await-in-loop
        const saved = await publishTemplate(card, brief);
        setPublishedIds((prev) => ({ ...prev, [id]: saved.id }));
      } catch (err) {
        failures.push(err && err.message ? err.message : 'Save failed');
      } finally {
        setBusy((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
    if (failures.length) setPublishError(failures[0]);
  };

  const remove = async (id) => {
    const templateId = publishedIds[id];
    if (templateId) {
      try {
        await deletePublishedTemplate(templateId);
      } catch (err) {
        setPublishError(err && err.message ? err.message : 'Delete failed');
        return; // Leave the card in place rather than hiding a live record.
      }
    }
    setCards((prev) => prev.filter((c) => c.id !== id));
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setPublishedIds((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const publishableCards = cards.filter((c) => c.publishable);

  return (
    <div data-testid="results-screen">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" /> Generated Templates
          <span className="text-sm font-normal text-gray-400">({cards.length})</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
        {cards.map((card) => (
          <ResultCard
            key={card.id}
            card={card}
            selected={selected.has(card.id)}
            published={published.has(card.id)}
            onToggleSelect={() => toggleSelected(card.id)}
            onPublish={publish}
            onDelete={remove}
            onOpenEditor={openInEditor}
            onPreview={setPreviewCard}
          />
        ))}
      </div>

      {publishError && (
        <div
          data-testid="publish-error"
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {publishError}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <Button
          onClick={() => publish([...selected])}
          disabled={selected.size === 0 || busy.size > 0}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Upload className="w-4 h-4 mr-2" /> Publish Selected ({selected.size})
        </Button>
        <Button
          variant="outline"
          onClick={() => publish(publishableCards.map((c) => c.id))}
          disabled={publishableCards.length === 0 || busy.size > 0}
        >
          <Upload className="w-4 h-4 mr-2" /> Publish All ({publishableCards.length})
        </Button>
        <Button variant="ghost" className="ml-auto" onClick={onGenerateMore}>
          <RotateCcw className="w-4 h-4 mr-2" /> Generate More
        </Button>
      </div>

      {previewCard && <CardModal card={previewCard} onClose={() => setPreviewCard(null)} />}
    </div>
  );
};

export default ResultsScreen;
